import { resolveBidderPreset } from '@auctisync/shared';
import { useEffect, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { ConnectionBar } from '../../components/bidder-web';
import { NotificationBell } from '../../components/NotificationBell';
import { SignOutButton } from '../../components/SignOutButton';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useConnection } from '../../hooks/useConnection';
import { houseLabel } from '@auctisync/api-client';
import { useSession } from '../../hooks/useSession';
import { isAuctionsCurrent, isLotsCurrent, type NavMatch } from './navActive';
import { useTenantBrandStyle } from '../../hooks/useTenantBrandStyle';

/**
 * The bidder-facing shell — nav, drawer, footer, connection bar.
 *
 * REBUILT to the web design handoff (2026-09-02). What it replaced was a light
 * Module #5 header with two links and no footer: a bidder could reach the
 * catalogue and their invoices, and nothing else. Six destinations, a search
 * pill, a house plate that says whose site this is, and a footer that answers
 * "how does this work" and "how do I reach you" without a support ticket.
 *
 * 🔴 THE HOUSE PLATE, NOT AN AUCTISYNC LOGO. On a Model B build this is the
 * tenant's site; the only AuctiSync mention anywhere on it is "Powered by
 * AuctiSync" in the legal strip. The header used to lead with our own mark,
 * which is the wrong answer to "whose auction is this?".
 *
 * RESPONSIVE VIA CONTAINER QUERIES (see `bidder-web.css`) rather than viewport
 * ones, per the handoff — the surface has to work embedded, in a split view and
 * beside a rail, and a viewport query answers a question nobody asked in any of
 * those cases.
 */

/**
 * The handoff's six destinations — all of them, all built (2026-09-03).
 *
 * They arrived one at a time, each in the commit that added its page, because
 * a dead link in the one component every page renders is worse than a short
 * nav: a bidder who clicks "Watchlist" and gets a 404 learns the site is
 * broken, not that a feature is coming. If a seventh is ever added, add it the
 * same way.
 *
 * THE ACCOUNT IS NOT IN HERE. It sits behind the account button on the right,
 * where the design puts it — the nav is for the sale, and the five things here
 * are all ways of looking at lots.
 */
/*
 * 🔴 "Browse" AND "Live now" BECAME ONE ENTRY (Jim, 2026-09-14): *"There is no
 * need to have Browse and Live."* Two nav items pointing at two pages that
 * showed the same lots was the clearest symptom of the split. The label is
 * "Lots", confirmed by Jim the same day.
 *
 * 🔴 "Sales" BECAME "Auctions" (Jim, 2026-09-14): *"'Sales' should be
 * 'Auctions' in the nav and anywhere that points to 'Sales'."* The route stays
 * `/auctions`, which is what it always was — only the word a bidder reads
 * changes, and it now matches the path.
 *
 * 🔴 EACH ENTRY CARRIES ITS OWN MATCHER, because `NavLink`'s prefix match got
 * this wrong: `/auctions` with no `end` matched `/auctions/:slug/live`, so
 * clicking Lots lit up Auctions. See `navActive.ts` — the rules are tested at
 * their boundaries there rather than inferred from `to`.
 */
const NAV: { to: string; label: string; isCurrent: NavMatch }[] = [
  { to: '/', label: 'Home', isCurrent: (p) => p === '/' },
  { to: '/auctions', label: 'Auctions', isCurrent: isAuctionsCurrent },
  { to: '/live', label: 'Lots', isCurrent: isLotsCurrent },
  { to: '/watchlist', label: 'Watchlist', isCurrent: (p) => p.startsWith('/watchlist') },
  { to: '/help', label: 'Help', isCurrent: (p) => p.startsWith('/help') },
];

/*
 * 🔴 HELP IS IN THE SITE NOW, not a link to help.auctisync.com.
 *
 * On a Model B build, sending a bidder to ANOTHER COMPANY'S DOMAIN to find out
 * how to bid at this one is the loudest possible statement that the site is not
 * really the house's. `/help` reads the same corpus, scoped to the house, so a
 * house that rewrote an article gets its own wording — which an external link
 * could never do.
 */
/**
 * Pages that render WITHOUT the nav and footer.
 *
 * The design is explicit that the auth split suppresses both, and the reason is
 * not tidiness: offering "Your bidding" to a half-signed-in visitor is an
 * invitation to a page that will bounce them straight back here. These screens
 * have exactly one thing to do.
 */
const CHROMELESS = ['/signin', '/signup', '/forgot-password', '/auth'];

function isChromeless(pathname: string): boolean {
  return CHROMELESS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

/** Initials for the house plate, from whatever name we actually have. */
function plateMark(name: string | null | undefined): string {
  const words = (name ?? 'AuctiSync').trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? 'A';
  const second = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  return (first + second).toUpperCase();
}

export function PublicLayout() {
  const { session } = useSession();
  const navigate = useNavigate();
  // `active` is the chosen house, or — for a signed-out visitor who arrived on
  // a lot from a search result — the one the page discovered from its auction.
  // The chrome on a white-label site must name the house even when the reader
  // has no account with it. `selected` still governs scoping and routing.
  const { houses, selected, active, clearSelection } = useBidderHouses();
  const { state: connection } = useConnection();
  const { pathname } = useLocation();
  const chromeless = isChromeless(pathname);
  const [menuOpen, setMenuOpen] = useState(false);

  const [mode, setMode] = useState<'light' | 'dark'>(() =>
    typeof localStorage !== 'undefined' && localStorage.getItem('bidder-mode') === 'dark'
      ? 'dark'
      : 'light',
  );
  useEffect(() => {
    try {
      localStorage.setItem('bidder-mode', mode);
    } catch {
      // ignore storage failures (private mode, etc.)
    }
  }, [mode]);

  // Tenant branding (spec §7): override --brand-* for the selected house. Only
  // the brand tokens change; neutrals + semantic/status colors stay AuctiSync's.
  // Since 2026-09-02 this also carries the derived --cta-*, from the same
  // function the native app uses.
  const brandStyle = useTenantBrandStyle(active?.branding, mode);
  // The house's chosen art direction, from the same `metadata.branding` bag the
  // colours come from — so a visitor arriving cold on a lot gets the house's
  // look, not just its colours.
  const preset = resolveBidderPreset(
    (active?.branding as { preset?: unknown } | null | undefined)?.preset,
  );

  const houseName = active === null ? 'AuctiSync' : houseLabel(active);
  const guest = session === null;

  return (
    <div
      data-theme="bidder"
      /* The house's art direction. Type and shape only — never colour, never
         the position of a box — so a tenant's choice cannot break a page.
         `resolveBidderPreset` falls back to modern rather than passing an
         unknown value to a CSS selector that would match nothing. */
      data-preset={preset}
      data-mode={mode}
      /* Read by CSS to freeze every countdown and drain the urgency colour at
         once. A clock that keeps ticking while we cannot reach the server is
         the page lying about the one number that decides whether a bidder still
         has time. */
      data-conn={connection}
      className="min-h-screen"
      style={{ ...brandStyle, background: 'var(--surface)', color: 'var(--text)' }}
    >
      {chromeless ? null : (
        <header className="w-nav">
          <div className="w-nav-in">
            <Link to="/" className="w-brand" aria-label={`${houseName} home`}>
              <span className="w-plate">{plateMark(houseName)}</span>
              <span style={{ textAlign: 'left' }}>
                <span className="w-bname" style={{ display: 'block' }}>
                  {houseName}
                </span>
                {/* Only shown once a house is selected — an empty second line
                  under the name reads as a missing field. */}
                {active ? <span className="w-bsub">Auction house</span> : null}
              </span>
            </Link>

            <nav className="w-links" aria-label="Primary">
              {NAV.map((item) => (
                /* 🔴 `Link`, NOT `NavLink`. NavLink computes its own current
                   state from a prefix match, which is the bug: `/auctions`
                   matched `/auctions/:slug/live`. The entry's own matcher
                   decides, and the attribute the CSS keys off is set from it. */
                <Link
                  key={item.to}
                  to={item.to}
                  className="w-link"
                  aria-current={item.isCurrent(pathname) ? 'page' : undefined}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="w-navr">
              <Link to="/search" className="w-srch" aria-label="Search the catalogue">
                <span aria-hidden>⌕</span>
                <span>Search the catalogue</span>
              </Link>

              <button
                type="button"
                onClick={() => setMode((m) => (m === 'dark' ? 'light' : 'dark'))}
                aria-label={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
                className="w-ico"
              >
                {/* Fixed box so the glyph swap cannot resize the control and
                  shift the whole bar between modes. */}
                {mode === 'dark' ? '☀' : '☾'}
              </button>

              {guest ? (
                <Link to="/signin" className="w-btn w-btn-cta w-btn-sm">
                  Sign in
                </Link>
              ) : (
                <>
                  {/* Only offered to someone who actually has a choice to make. */}
                  {selected && houses.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => {
                        clearSelection();
                        void navigate('/');
                      }}
                      title="Switch auction house"
                      className="w-btn w-btn-out w-btn-sm"
                    >
                      Switch house
                    </button>
                  ) : null}
                  <NotificationBell />
                  <Link to="/my/dashboard" className="w-ico" aria-label="Your account">
                    ☰
                  </Link>
                </>
              )}

              <button
                type="button"
                className="w-burger"
                aria-label="Menu"
                aria-expanded={menuOpen}
                onClick={() => setMenuOpen((m) => !m)}
              >
                {menuOpen ? '✕' : '☰'}
              </button>
            </div>
          </div>

          {/*
          Below 1100px the inline links are gone, so THIS IS the navigation. It
          carries the same five destinations at full-row touch height, plus the
          account or register row — the handoff's rule that reveal and hide
          happen in the same query, because a band with neither strands tablet
          users.
        */}
          {menuOpen ? (
            <div className="w-menu">
              {NAV.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  className="w-menu-i"
                  aria-current={item.isCurrent(pathname) ? 'page' : undefined}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                  <span aria-hidden>›</span>
                </Link>
              ))}
              <Link
                to={guest ? '/register' : '/my/dashboard'}
                className="w-menu-i"
                onClick={() => setMenuOpen(false)}
              >
                {guest ? 'Register to bid' : 'Account'}
                <span aria-hidden>›</span>
              </Link>
            </div>
          ) : null}

          {/* Under the nav so it pushes nothing off screen. */}
          <ConnectionBar houseName={houseName} />
        </header>
      )}

      <main>
        <Outlet />
      </main>

      {chromeless ? null : (
        <footer className="w-foot">
          <div className="w-foot-in">
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 11, marginBottom: 13 }}>
                <span className="w-plate">{plateMark(houseName)}</span>
                <span className="w-bname">{houseName}</span>
              </div>
              <p className="w-body muted" style={{ fontSize: 14, maxWidth: 330 }}>
                Online timed auctions. Collection in person — every lot is collected, not shipped.
              </p>
            </div>

            <div>
              <h4>Bidding</h4>
              <ul>
                <li>
                  <Link to="/live">All lots</Link>
                </li>
                <li>
                  <Link to="/auctions">Current auctions</Link>
                </li>
                <li>
                  <Link to={guest ? '/register' : '/my/dashboard'}>
                    {guest ? 'Register to bid' : 'Your bidding'}
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4>How it works</h4>
              {/* The same three the app teaches in first run and links from every
                lot page. One corpus, one set of answers. */}
              <ul>
                <li>
                  <Link to="/help/how-maximum-bids-work">Maximum bids</Link>
                </li>
                <li>
                  <Link to="/help/anti-snipe-for-bidders">Why timers extend</Link>
                </li>
                <li>
                  <Link to="/help/buyer-premium-explained">Buyer&apos;s premium</Link>
                </li>
              </ul>
            </div>

            <div>
              <h4>Contact</h4>
              <ul>
                <li>
                  <Link to="/my/invoices">Your invoices</Link>
                </li>
                <li>
                  <Link to="/help">Help centre</Link>
                </li>
              </ul>
            </div>
          </div>

          <div className="w-legal">
            <span>
              © {new Date().getFullYear()} {houseName}. All sales final.
            </span>
            {/* The ONLY AuctiSync mention on a house build. */}
            <span>Powered by AuctiSync</span>
          </div>
        </footer>
      )}

      {session && !chromeless ? (
        <div className="w-wrap" style={{ paddingBottom: 24, textAlign: 'right' }}>
          <SignOutButton />
        </div>
      ) : null}
    </div>
  );
}
